import { Fragment } from 'react';
import cn from 'classnames';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { SelectIcon, CheckIcon } from '..';
import * as styles from './styles';

interface DropdownProps {
  children: React.ReactNode;
  containerClassName?: string;
  buttonContent?: React.ReactNode;
  buttonClassName?: string;
  isButtonUnstyled?: boolean;
  ButtonLeftIcon?: React.ElementType;
  buttonText?: string;
  ariaButtonText?: string;
  isFullWidth?: boolean;
}

export const DropdownMenu = ({
  children,
  containerClassName = '',
  buttonContent,
  buttonClassName,
  isButtonUnstyled = false,
  ButtonLeftIcon,
  buttonText = 'Options',
  ariaButtonText,
  isFullWidth = false,
}: DropdownProps) => {
  return (
    <Menu
      as="div"
      className={cn('relative inline-block text-left', containerClassName, {
        'w-full': isFullWidth,
      })}
    >
      <div>
        <Menu.Button
          className={cn(
            { [styles.button]: !isButtonUnstyled, 'w-full': isFullWidth },
            buttonClassName
          )}
          aria-label={ariaButtonText ? `${ariaButtonText} ${buttonText}` : ''}
        >
          {buttonContent || (
            <>
              {ButtonLeftIcon && (
                <ButtonLeftIcon className={styles.icon} aria-hidden="true" />
              )}
              {buttonText}
              <SelectIcon
                className={cn(styles.icon, 'ml-auto')}
                aria-hidden="true"
              />
            </>
          )}
        </Menu.Button>
      </div>

      <Transition as={Fragment} {...styles.transitions}>
        <Menu.Items
          className={cn(
            styles.container,
            'max-h-60 origin-top-right overflow-auto',
            isFullWidth ? 'w-full' : 'w-56'
          )}
        >
          <div className="py-1">{children}</div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

interface MenuItemProps {
  as: typeof Link | 'button';
  LeftIcon?: React.ElementType;
  children: React.ReactNode;
  ariaLabel?: string;
  selected?: boolean;
}

export const MenuItem = ({
  as,
  LeftIcon,
  children,
  ariaLabel,
  selected = false,
  ...rest
}: MenuItemProps & React.ComponentProps<typeof Menu.Item>) => {
  return (
    <Menu.Item
      as={as}
      {...rest}
      className={cn(styles.item, {
        'font-bold': selected,
      })}
      aria-current={selected}
      aria-label={ariaLabel}
    >
      {LeftIcon && <LeftIcon className={styles.icon} aria-hidden="true" />}
      {children}
      {selected && (
        <CheckIcon
          className={cn(styles.iconBase, 'ml-auto text-green-600')}
          aria-hidden="true"
        />
      )}
    </Menu.Item>
  );
};
